/**
 * 70.5 and 74.4 (DECISIONS 350, 351; ARCHITECTURE §5.39 (4)): what each biome is made of — `BiomeDressing`, keyed by
 * `BiomeId`, lane H's data. The battle's stands (core/biomes.ts `dressBattleSite`) and the planet's forest (core/forest.ts)
 * read it where they read `BIOMES[id].trees / rocks`; 74.3's biome field will say where and how dense, this says what.
 *
 * Each row is today's (core/biomes.ts `BIOMES`, the ground cover rendering/biomeKit.ts drew) joined to the dressing sheet's
 * proposal (design_sheets/dressing.ts `ADD`, as the owner took it: all ten new families, the sheet's lean for the rocks and
 * the pieces) — §5.39's shape with the two fields the sheet added:
 *   - `trees`: weighted families (today's and 70.5's);
 *   - `rocks`: the field's kinds (a rock field's, an objective's field's) and its density, and `rim`, the kinds a stand
 *     lays at its edge (today's `BIOMES[id].rocks`), each with 74.4's new kinds;
 *   - `cover`: the flat flora drawn at a tree's foot (decoration, never in state);
 *   - `pieces`: what lies beside a stand tree, each a chance a tree — a log or a termite mound (cover), a shrub
 *     (concealment), a fern bed, a glowcap ring or old bones (drawn only, never in state: rendering/treeStands.ts lays them
 *     at the tree's foot from its id, as the cover);
 *   - `weather`: what lies on a rock's upward faces — moss, lichen, dust or ash (drawn only, the rock's vertex colours);
 *   - `perMaterial`: a ground material's own trees (the willow and mangrove on mud, the krummholz on bare rock).
 */
import type { BiomeId, FloraType, GroundMaterial, RockType } from '../types';
import { BIOMES } from './biomes';

/** 74.4's dressing pieces: what each is to the sim — cover (log, mound), concealment (shrub), drawn only (the rest). */
export type DressingPiece = 'log' | 'mound' | 'shrub' | 'fern' | 'glowcap' | 'bones';
/** The pieces the sim holds (a flora or a rock in state); the rest are drawn only. */
export type SimPiece = 'log' | 'mound' | 'shrub';
export const isSimPiece = (p: DressingPiece): p is SimPiece => p === 'log' || p === 'mound' || p === 'shrub';
/** The pieces drawn at a tree's foot and never in state. */
export type DrawnPiece = Exclude<DressingPiece, SimPiece>;
export const DRAWN_PIECES: readonly DrawnPiece[] = ['fern', 'glowcap', 'bones'];
export type RockWeather = 'moss' | 'lichen' | 'dust' | 'ash';

export interface BiomeDressing {
  trees: Array<[FloraType, number]>;
  rocks: {
    kinds: Array<[RockType, number]>;
    rim: Array<[RockType, number]>;
    field: { chance: number; count: [number, number]; radius: number } | null;
  };
  cover: FloraType[];
  pieces: Array<[DressingPiece, number]>;
  weather: RockWeather;
  perMaterial?: Partial<Record<GroundMaterial, { trees: Array<[FloraType, number]> }>>;
}

/** Today's ground cover a biome (moved from rendering/biomeKit.ts BIOME_GROUND, which reads it). */
const COVER: Record<BiomeId, FloraType[]> = {
  broadleaf: ['velvetMatgrass'], firStand: ['velvetMatgrass'], heath: ['ghostReed'],
  savanna: ['thermocapLichen'], ashBarrens: ['thermocapLichen'], lanternGrove: ['velvetMatgrass', 'lanternBracket'],
  birchGlade: ['velvetMatgrass'], burn: ['thermocapLichen'], scrub: ['thermocapLichen'], hollowWood: ['ghostReed', 'lanternBracket'],
};

/** The sheet's proposal a biome, as ruled (350, 351). */
const ADD: Record<BiomeId, { trees: Array<[FloraType, number]>; rocks: Array<[RockType, number]>; pieces: Array<[DressingPiece, number]>; weather: RockWeather; perMaterial?: BiomeDressing['perMaterial'] }> = {
  // (421: the species beside their families' weights — the elm 2, the maple 1 (an understory tree), the three oaks 1 each; the sheet's lean, the owner tuning by eye)
  // (423: the aspen, the weeping willow and the chestnut 1 each beside them, the willow 3 on mud with the drape)
  broadleaf: { trees: [['drapeWillow', 1], ['stiltMangrove', 0.5], ['vaseElm', 2], ['japaneseMaple', 1], ['spreadingOak', 1], ['roundOak', 1], ['pinOak', 1], ['quakingAspen', 1], ['weepingWillow', 1], ['chestnut', 1]], rocks: [['split', 1]], pieces: [['shrub', 0.25], ['fern', 0.5], ['log', 0.06]], weather: 'moss',
    perMaterial: { mud: { trees: [['drapeWillow', 3], ['stiltMangrove', 2], ['weepingWillow', 3]] } } },
  firStand: { trees: [['pillarRedwood', 1], ['goldLarch', 1]], rocks: [['split', 1]], pieces: [['fern', 0.4], ['log', 0.1]], weather: 'moss' },
  birchGlade: { trees: [['goldLarch', 1], ['drapeWillow', 0.5], ['japaneseMaple', 1], ['quakingAspen', 2]], rocks: [['split', 1]], pieces: [['shrub', 0.2], ['fern', 0.3], ['log', 0.04]], weather: 'moss' },
  burn: { trees: [], rocks: [], pieces: [['log', 0.15]], weather: 'ash' },
  heath: { trees: [['krummholz', 2], ['candleCypress', 1]], rocks: [['ledge', 3], ['split', 1]], pieces: [['shrub', 0.3]], weather: 'lichen',
    perMaterial: { rock: { trees: [['krummholz', 4]] } } },
  savanna: { trees: [['parasolThorn', 3]], rocks: [['kopje', 3]], pieces: [['shrub', 0.2], ['mound', 0.15], ['bones', 0.03]], weather: 'dust' },
  scrub: { trees: [['parasolThorn', 1], ['candleCypress', 1], ['dragonBlood', 1]], rocks: [['kopje', 2], ['ledge', 2]], pieces: [['shrub', 0.3], ['mound', 0.08]], weather: 'dust' },
  ashBarrens: { trees: [['dragonBlood', 2]], rocks: [['ledge', 2]], pieces: [['bones', 0.05]], weather: 'ash' },
  lanternGrove: { trees: [['treeFern', 2], ['ghostCap', 1]], rocks: [['crystal', 1], ['split', 1]], pieces: [['fern', 0.4], ['glowcap', 0.3]], weather: 'moss' },
  hollowWood: { trees: [['ghostCap', 2], ['treeFern', 1]], rocks: [['crystal', 2]], pieces: [['glowcap', 0.4], ['log', 0.06]], weather: 'lichen' },
};

function dressingOf(b: BiomeId): BiomeDressing {
  const d = BIOMES[b], a = ADD[b], field = d.rockField ?? null;
  return {
    trees: [...d.trees, ...a.trees],
    rocks: { kinds: [...(field?.kinds ?? d.rocks), ...a.rocks], rim: [...d.rocks, ...a.rocks], field: field ? { chance: field.chance, count: field.count, radius: field.radius } : null },
    cover: COVER[b],
    pieces: a.pieces, weather: a.weather, ...(a.perMaterial ? { perMaterial: a.perMaterial } : {}),
  };
}

const rows = new Map<BiomeId, BiomeDressing>();
/** A biome's dressing (made at first use: core/biomes.ts imports this module, and the rows read its BIOMES). */
export function biomeDressing(b: BiomeId): BiomeDressing {
  let d = rows.get(b);
  if (!d) { d = dressingOf(b); rows.set(b, d); }
  return d;
}

/** A biome's trees on a ground material: its `perMaterial` row's where it has one, else its own. */
export function treesOn(biome: BiomeId, material: GroundMaterial | null): Array<[FloraType, number]> {
  const d = biomeDressing(biome);
  return (material && d.perMaterial?.[material]?.trees) || d.trees;
}
